import MaintenanceOverlay from "@components/utils/MaintenanceOverlay";
import { GameProviderModel, PropsWithClassName, TabNavigation, TabNavigationAction, cn } from "kz-ui-sdk";
import { CSSProperties, PropsWithChildren, useMemo } from "react";
import { useGameDataSource } from "../../hooks";
import { standardizeGameStats } from "../../utils";

interface ProviderListProps extends PropsWithChildren, PropsWithClassName {
  onSelect?: (provider?: GameProviderModel) => void;
  selected?: GameProviderModel;
  categoryId?: string;
  style?: CSSProperties;
}

const GameProviderList = ({ className, onSelect, selected, categoryId, style }: ProviderListProps) => {
  const { providersStatsResponse, providersResponse } = useGameDataSource();

  const providers = useMemo(() => {
    if (providersStatsResponse) {
      const standardizedStats = standardizeGameStats(providersStatsResponse);
      return (providersResponse?.entries || [])
        .map((provider) => ({
          ...provider,
          total: categoryId ? standardizedStats?.[provider.id]?.[categoryId] ?? 0 : 0,
        }))
        .filter((provider) => provider.total !== 0);
    }
  }, [categoryId, providersResponse?.entries, providersStatsResponse]);

  const providerOptions: TabNavigationAction[] = useMemo(() => {
    return (
      providers?.map(
        (provider) =>
          ({
            id: provider.id,
            label: !!provider.primaryImage?.url ? undefined : (
              <MaintenanceOverlay
                underMaintenance={provider.isMaintenance}
                className="rounded-r-lg"
              >
                {provider.name}
              </MaintenanceOverlay>
            ),
            icon: !!provider.primaryImage?.url && (
              <MaintenanceOverlay
                underMaintenance={provider.isMaintenance}
                className="rounded-r-lg"
              >
                <img
                  src={provider.primaryImage?.url}
                  alt={provider.name}
                  className="h-full max-h-[40px] w-auto max-w-[60px] object-contain object-center"
                  aria-label={`Game provider ${provider.name} icon`}
                />
              </MaintenanceOverlay>
            ),
            styles: {
              className: "relative",
            },
            ["aria-label"]: `Game provider ${provider.name}`,
          }) satisfies TabNavigationAction,
      ) ?? []
    );
  }, [providers]);

  const selectedOption = useMemo(() => {
    return providerOptions.find((option) => option.id === selected?.id);
  }, [selected, providerOptions]);

  return (
    <div
      className={cn(className)}
      style={style}
    >
      <TabNavigation
        onChange={(tab) => {
          if (!tab) {
            onSelect?.(undefined);
            return;
          }
          const provider = providers?.find((provider) => provider.id === tab.id);
          onSelect?.(provider);
        }}
        currentTab={selectedOption}
        tabActions={providerOptions}
        direction={"vertical"}
        variant="secondary"
        classes={{
          "tab&": "w-[85px] py-0.5 px-5 rounded-l-none",
        }}
      />
    </div>
  );
};

export default GameProviderList;
